import { ZONES } from '@/models/search-filters';

export const getZoneName = (zone) => {
  return zone
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(' ', '_');
};

export const mapToZones = (zones) => {
  const results = {};

  Object.keys(zones).forEach((country) => {
    const countryKey = Object.keys(ZONES).find((key) => ZONES[key].name.find((c) => c === country));
    if (countryKey && !(countryKey in results)) {
      results[`${countryKey}`] = {};
    }

    if (JSON.stringify(zones[country]) === '{}') {
      const zoneKeys = Object.keys(ZONES[`${countryKey}`].zones);
      if (zoneKeys) {
        zoneKeys.forEach((zone) => {
          results[`${countryKey}`][`${zone}`] = [];
        });
      }
    }
    Object.keys(zones[country]).forEach((zone) => {
      const zoneKey = Object.keys(ZONES[`${countryKey}`].zones).find((key) =>
        ZONES[countryKey].zones[key].find((z) => z === zone),
      );
      if (zoneKey && !(zoneKey in results[`${countryKey}`])) {
        results[`${countryKey}`][`${zoneKey}`] = [];
      }
    });
  });
  return results;
};

export const expandToZones = (zones) => {
  const results = {};

  Object.keys(zones).forEach((country) => {
    const countryValues = ZONES[`${country}`].name;
    if (countryValues) {
      countryValues.forEach((v) => (results[`${v}`] = {}));
    }

    // Whole country: when every province is selected, send just the country level
    // ({ Canada: {} }) instead of listing them all — the backend treats an empty region
    // map as "the entire country" (same as United States, which has no sub-regions).
    const allProvinces = Object.keys(ZONES[`${country}`].zones);
    const selectedProvinces = Object.keys(zones[country]);
    if (allProvinces.length > 0 && selectedProvinces.length === allProvinces.length) {
      return;
    }

    Object.keys(results).forEach((countryName) => {
      const countryKey = Object.keys(ZONES).find((key) => ZONES[key].name.find((c) => c === countryName));
      Object.keys(zones[country]).forEach((zone) => {
        const zoneValues = ZONES[countryKey].zones[`${zone}`];

        if (zoneValues) {
          zoneValues.forEach((z) => (results[countryName][`${z}`] = []));
        }
      });
    });
  });
  return results;
};

export const getZonesCount = (zones) => {
  let count = 0;

  if (zones.CANADA) {
    Object.keys(zones.CANADA).forEach(() => (count = count + 1));
  }
  if (zones.UNITED_STATES) {
    count = count + 1;
  }
  return count;
};

// Order-independent canonical string of a zones *selection*, used to detect real changes.
// The one bit of domain nuance — whether a country's empty-object value means "selected as a whole"
// or "nothing selected" — is derived from the ZONES schema, not hard-coded per country:
//   - A country with sub-regions in ZONES (e.g. Canada) is a container: it contributes one entry per
//     selected sub-region, so an empty `{}` (e.g. residue after unchecking the last province) is "no
//     selection".
//   - A country with no sub-regions in ZONES (e.g. United States) is a leaf: its mere presence is the
//     selection, so `{ …: {} }` counts.
// Adding another country later needs no change here — it's classified by its schema entry.
const canonicalizeZones = (zones) => {
  const z = zones || {};
  const parts = [];
  Object.keys(z).forEach((country) => {
    const selection = z[country];
    const schemaZones = (ZONES[country] && ZONES[country].zones) || {};
    const isContainer = Object.keys(schemaZones).length > 0;
    if (isContainer) {
      if (selection && typeof selection === 'object' && !Array.isArray(selection)) {
        Object.keys(selection)
          .sort()
          .forEach((sub) => {
            const val = selection[sub];
            const valKey = Array.isArray(val) ? [...val].map(String).sort().join(',') : JSON.stringify(val);
            parts.push(country + '/' + sub + ':' + valKey);
          });
      }
    } else if (selection) {
      parts.push(country);
    }
  });
  return parts.sort().join('|');
};

export const areZonesEqual = (a, b) => canonicalizeZones(a) === canonicalizeZones(b);
